import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accept } from 'react-dropzone';
import { computeText } from '@/locales/utils';
import { Text } from '@/components/atoms/Text';
import { FileInput } from '@/components/form/FileInput';
import {
    Container,
    DescriptionItem,
    DescriptionList,
    DropzoneContainer,
    Link,
    Section,
    SectionTitle,
} from './UploadStep.style';
import { useUploadStepVM } from './UploadStep.viewmodel';

const ALLOWED_FILE_TYPES: Accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

type UploadStepProps = {
    brandId: number;
};

export const UploadStep: React.FunctionComponent<UploadStepProps> = ({ brandId }) => {
    const intl = useIntl();
    const { onDownloadTemplate, onExportCustomers, isExporting } = useUploadStepVM({ brandId });

    return (
        <Container>
            <Section>
                <SectionTitle>
                    <FormattedMessage id="customer.modal.import.addNew.title" />
                </SectionTitle>
                <DescriptionList>
                    <DescriptionItem>
                        <FormattedMessage
                            id="customer.modal.import.addNew.step1"
                            values={{
                                link: (
                                    <Link onClick={onDownloadTemplate}>
                                        <FormattedMessage id="customer.modal.import.addNew.step1.link" />
                                    </Link>
                                ),
                            }}
                        />
                    </DescriptionItem>
                    <DescriptionItem>
                        <FormattedMessage id="customer.modal.import.addNew.step2" />
                    </DescriptionItem>
                    <DescriptionItem>
                        <FormattedMessage id="customer.modal.import.addNew.step3" />
                    </DescriptionItem>
                </DescriptionList>
            </Section>

            <Section>
                <SectionTitle>
                    <FormattedMessage id="customer.modal.import.update.title" />
                </SectionTitle>
                <DescriptionList>
                    <DescriptionItem>
                        <FormattedMessage
                            id="customer.modal.import.update.step1"
                            values={{
                                link: (
                                    <Link onClick={onExportCustomers} style={{ opacity: isExporting ? 0.5 : 1 }}>
                                        {isExporting ? (
                                            <FormattedMessage id="customer.modal.import.update.step1.loading" />
                                        ) : (
                                            <FormattedMessage id="customer.modal.import.update.step1.link" />
                                        )}
                                    </Link>
                                ),
                            }}
                        />
                    </DescriptionItem>
                    <DescriptionItem>
                        <FormattedMessage id="customer.modal.import.update.step2" />
                    </DescriptionItem>
                    <DescriptionItem>
                        <FormattedMessage id="customer.modal.import.update.step3" />
                    </DescriptionItem>
                </DescriptionList>
                <Text text="customer.modal.import.update.warning" type="small" />
            </Section>

            <DropzoneContainer>
                <React.Suspense>
                    <FileInput
                        mode="compact"
                        accept={ALLOWED_FILE_TYPES}
                        maxFiles={1}
                        name="file"
                        rules={{
                            required: computeText(intl, 'field.required'),
                            validate: {
                                fileExists: (file) => !!file,
                                maxSize: (file) =>
                                    !file ||
                                    file.size <= MAX_FILE_SIZE ||
                                    computeText(intl, 'customer.modal.import.error.fileTooLarge'),
                            },
                        }}
                        transform={{
                            output: (file) => file[0],
                            input: (file: File) => (file ? [file] : []),
                        }}
                    />
                </React.Suspense>
            </DropzoneContainer>
        </Container>
    );
};
